// Considered: PurposeParentDialogMolecule's picker — rejected because it chooses a parent for one known
// sphere; this is the Center-wide worklist of spheres still waiting for one. See spec Reuse Audit.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { useTranslation } from 'localization';
import type { SpherePurpose, UnplacedSphere } from 'types/purpose';
import { listUnplacedSpheres } from '../../../api/purposeApi';
import { usePurposeNearby } from '../../../hooks/usePurposeNearby';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import PurposeParentDialogMolecule from '../PurposeParentDialog/PurposeParentDialogMolecule';
import './NeedsAHomeListMolecule.scss';

export interface NeedsAHomeListMoleculeProps {
  graphClient: ApiClient | null;
  /** The Center whose unplaced spheres are listed. */
  centerId: string;
  organizationId?: string;
  pageSize?: number;
  /** Called after a sphere is given a Purpose parent and leaves the list. */
  onPlaced?: (sphereId: string, purpose: SpherePurpose) => void;
}

type ListStatus = 'loading' | 'ready' | 'error';

const DEFAULT_PAGE_SIZE = 20;

const NeedsAHomeListMolecule: React.FC<NeedsAHomeListMoleculeProps> = ({
  graphClient,
  centerId,
  organizationId,
  pageSize = DEFAULT_PAGE_SIZE,
  onPlaced,
}) => {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<ListStatus>('loading');
  const [items, setItems] = useState<UnplacedSphere[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [target, setTarget] = useState<UnplacedSphere | null>(null);
  const requestId = useRef(0);
  // Keyed on the row the person clicked, so each sphere's own nearby candidates load (SC9).
  const nearby = usePurposeNearby(graphClient, target?.id ?? null, target !== null);

  // The link exists only while something needs a home, so the count is probed with a one-row page.
  // A failed probe leaves the link hidden: there is nothing the person can act on yet.
  useEffect(() => {
    if (!graphClient) return undefined;
    let cancelled = false;
    listUnplacedSpheres(graphClient, centerId, { limit: 1 })
      .then((page) => {
        if (!cancelled) setTotal(page.total);
      })
      .catch(() => {
        if (!cancelled) setTotal(0);
      });
    return () => {
      cancelled = true;
    };
  }, [graphClient, centerId]);

  const load = useCallback(
    (cursor?: string) => {
      if (!graphClient) return;
      requestId.current += 1;
      const mine = requestId.current;
      setStatus('loading');
      listUnplacedSpheres(graphClient, centerId, { limit: pageSize, ...(cursor ? { cursor } : {}) })
        .then((page) => {
          if (mine !== requestId.current) return;
          setItems((current) => (cursor ? [...current, ...page.items] : page.items));
          setNextCursor(page.nextCursor ?? undefined);
          setTotal(page.total);
          setStatus('ready');
        })
        .catch(() => {
          if (mine !== requestId.current) return;
          setStatus('error');
        });
    },
    [graphClient, centerId, pageSize]
  );

  const openList = () => {
    setItems([]);
    setNextCursor(undefined);
    setOpen(true);
    load();
  };

  const closeList = () => {
    requestId.current += 1;
    setOpen(false);
    setTarget(null);
  };

  const retry = () => load(items.length > 0 ? nextCursor : undefined);

  const handleMoved = (purpose: SpherePurpose) => {
    if (!target) return;
    const placed = target;
    setItems((current) => current.filter((item) => item.id !== placed.id));
    setTotal((current) => Math.max(0, current - 1));
    setTarget(null);
    onPlaced?.(placed.id, purpose);
  };

  if (total === 0 && !open) return null;

  return (
    <>
      {total > 0 && (
        <button type="button" className="needs-a-home__link" onClick={openList}>
          {t('purpose.needsHome.link', { count: total })}
        </button>
      )}
      <DialogWrapper
        header={t('purpose.needsHome.title')}
        visible={open}
        onHide={closeList}
        className="needs-a-home"
        style={{ width: 'min(40rem, 100vw)' }}
      >
        <div className="needs-a-home__body" data-testid="needs-a-home-list">
          {items.length > 0 && (
            <ul className="needs-a-home__list">
              {items.map((item) => (
                <li key={item.id} className="needs-a-home__row">
                  <GlyphAtom glyph={item.glyph} fallbackText={item.name} />
                  <span className="needs-a-home__name">{item.name}</span>
                  <ButtonAtom
                    variant="secondary"
                    size="small"
                    label={t('purpose.needsHome.chooseAction')}
                    aria-label={t('purpose.needsHome.chooseFor', { name: item.name })}
                    onClick={() => setTarget(item)}
                  />
                </li>
              ))}
            </ul>
          )}
          {status === 'loading' && (
            <output className="needs-a-home__loading" aria-label={t('purpose.needsHome.loading')}>
              <SkeletonAtom count={items.length > 0 ? 1 : 3} height="2rem" gap="var(--spacing-sm)" />
            </output>
          )}
          {status === 'error' && (
            <div className="needs-a-home__error" role="alert">
              <p className="needs-a-home__error-title">{t('purpose.needsHome.error')}</p>
              <p className="needs-a-home__error-reason">{t('purpose.needsHome.errorReason')}</p>
              <ButtonAtom variant="secondary" size="small" label={t('purpose.field.loadErrorAction')} onClick={retry} />
            </div>
          )}
          {status === 'ready' && items.length === 0 && <p className="needs-a-home__empty">{t('purpose.needsHome.empty')}</p>}
          {status === 'ready' && nextCursor && (
            <ButtonAtom variant="secondary" size="small" label={t('purpose.needsHome.loadMore')} onClick={() => load(nextCursor)} />
          )}
        </div>
      </DialogWrapper>
      {target && (
        <PurposeParentDialogMolecule
          visible
          onHide={() => setTarget(null)}
          graphClient={graphClient}
          sphereId={target.id}
          sphereName={target.name}
          organizationId={organizationId}
          siblings={nearby.siblings}
          nearbyChildren={nearby.children}
          nearbyLoading={nearby.loading}
          onMoved={handleMoved}
        />
      )}
    </>
  );
};

export default NeedsAHomeListMolecule;
